import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Btn from '../../../components/ui/Btn';
import TemplateEditor from '../../../components/ui/TemplateEditor';
import PromptAssistant from './PromptAssistant';
import { LivePreview, PromptTestData, VariablesMenu, promptScope, usePreviewSession } from './PromptExtras';
import { PROMPT_VARS } from './PreToolsEditor';
import { explainTemplate } from '../../../utils/templatePreview';
import { METHODS } from '../../../utils/toolDefinition';
import { varsFromSample } from '../../../utils/templateAssist';

const AUTOSAVE_MS = 1200;

// Instrucciones del agente. Se guardan solas en el borrador (al dejar de escribir y al salir del cuadro); los canales siguen
// usando la versión publicada hasta publicar. El estado del guardado va en una línea arriba del texto, sin barras que lo tapen.
export default function PromptPanel({ agent, draft, readOnly, onSave, focusSignal, engineReady, hasCredential, onGoCredential }) {
  const [text, setText] = useState(draft?.prompt || '');
  const [status, setStatus] = useState('saved');     // saved | dirty | saving | error
  const [error, setError] = useState('');
  const lastSaved = useRef(draft?.prompt || '');
  const timer = useRef(null);
  const area = useRef(null);
  const { sample, setSample, run, setRun } = usePreviewSession(agent.id);
  const [split, setSplit] = useState(() => { try { return localStorage.getItem('prompt.preview') !== 'off'; } catch { return true; } });
  const toggleSplit = () => setSplit((v) => { try { localStorage.setItem('prompt.preview', v ? 'off' : 'on'); } catch { /* sin almacenamiento */ } return !v; });

  // Si el borrador cambió por fuera (publicar, copiar una versión), se toma; si es solo el eco de nuestro guardado, no se toca lo escrito.
  useEffect(() => {
    const incoming = draft?.prompt || '';
    if (incoming !== lastSaved.current) { lastSaved.current = incoming; setText(incoming); setStatus('saved'); }
  }, [draft?.id, draft?.prompt]);

  useEffect(() => { if (focusSignal) area.current?.focus(); }, [focusSignal]);

  const save = useCallback(async (value) => {
    clearTimeout(timer.current);
    if (readOnly || value === lastSaved.current) { setStatus('saved'); return; }
    setStatus('saving'); setError('');
    try {
      lastSaved.current = value;
      await onSave(value);
      setStatus((s) => (s === 'saving' ? 'saved' : s));
    } catch (e) { lastSaved.current = ''; setError(e.message); setStatus('error'); }
  }, [onSave, readOnly]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const onChange = (v) => {
    setText(v); setStatus('dirty');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(v), AUTOSAVE_MS);
  };
  // Un texto que llega del asistente (propuesta aprobada o «Deshacer») se guarda al momento, como cualquier edición.
  const applyText = (value) => { setText(value); setStatus('dirty'); clearTimeout(timer.current); save(value); };
  // Inserta un dato en el cursor (o al final si el cuadro no tiene foco) y deja el cursor después.
  const insert = (snippet) => { if (area.current) area.current.insert(snippet); else applyText(text + snippet); };
  const preTools = useMemo(() => (Array.isArray(draft?.config?.pre_tools) ? draft.config.pre_tools : []), [draft]);
  // Lo que el motor no entiende en el prompt (por ejemplo expresiones de n8n) se marca en rojo con su motivo.
  const preNames = useMemo(() => preTools.map((p) => p.as).filter(Boolean), [preTools]);
  const explain = useMemo(() => { const m = new Set(METHODS.map((x) => x.name)); return (e) => explainTemplate(e, preNames, m); }, [preNames]);
  const scope = useMemo(() => promptScope(agent, sample, run), [agent, sample, run]);
  // Lo que ofrece el autocompletado: el contacto de prueba, los datos previos (con sus campos reales si ya se probaron) y las etiquetas
  // en español de las variables conocidas.
  const vars = useMemo(() => {
    const labels = Object.fromEntries(PROMPT_VARS.map((v) => [v.path, v.label]));
    const list = varsFromSample(scope).map((v) => ({ ...v, label: labels[v.path] || v.label }));
    for (const p of preTools) for (const [k, l] of [['texto', 'su respuesta'], ['ok', '¿funcionó?'], ['nodos', 'salida de cada paso']]) {
      const path = `datos.${p.as}.${k}`;
      if (!list.some((v) => v.path === path)) list.push({ path, label: `${p.as}: ${l}` });
    }
    return list;
  }, [scope, preTools]);

  const label = { saved: text.trim() ? 'Guardado en el borrador' : 'Vacías', dirty: 'Escribiendo…', saving: 'Guardando…', error: 'No se guardó' }[status];

  const tone = status === 'error' ? 'text-signal' : status === 'saved' && text.trim() ? 'text-live' : 'text-fg-faint';

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2">
        <span id="prompt-label" className="text-[13px] font-semibold text-fg">Instrucciones del agente</span>
        <span role="status" aria-live="polite" className={`inline-flex items-center gap-1.5 text-[12.5px] ${tone}`}>
          <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${status === 'error' ? 'bg-signal' : status === 'saved' && text.trim() ? 'bg-live' : 'bg-fg-faint'} ${status === 'saving' ? 'animate-pulse' : ''}`} />
          {label}
        </span>
        {status === 'error' && <Btn size="sm" variant="ghost" onClick={() => save(text)}>Reintentar</Btn>}
        <button type="button" onClick={toggleSplit} aria-pressed={split} className="text-[12.5px] text-fg-dim hover:text-fg rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">{split ? 'Ocultar vista previa' : 'Ver vista previa'}</button>
        <span className="ml-auto text-[12.5px] text-fg-faint tabular-nums">{text.length.toLocaleString('es-MX')} caracteres</span>
        <VariablesMenu preTools={preTools} onInsert={insert} disabled={readOnly} />
        <details className="relative group">
          <summary className="list-none cursor-pointer text-[12.5px] text-fg-dim hover:text-fg rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 [&::-webkit-details-marker]:hidden">Qué escribir</summary>
          <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl bg-panel ring-1 ring-line p-4 text-[13px] leading-relaxed text-fg-dim" style={{ boxShadow: 'var(--shadow)' }}>
            <ul className="space-y-1.5 list-disc pl-4 marker:text-fg-faint">
              <li>Para quién trabaja y qué resuelve.</li>
              <li>Cómo habla: tono, trato, largo de las respuestas.</li>
              <li>Qué datos debe pedir y en qué orden.</li>
              <li>Qué no debe hacer o prometer.</li>
              <li>Cuándo pasar la conversación a una persona.</li>
            </ul>
          </div>
        </details>
      </div>
      {/* La vista previa va a un lado solo en pantallas anchas (xl); en las demás, debajo del editor y siempre visible. */}
      <div className={split ? 'grid gap-4 xl:grid-cols-2 xl:items-stretch' : ''}>
        <TemplateEditor ref={area} id="prompt" value={text} onChange={onChange} onBlur={() => save(text)} onSave={() => save(text)} readOnly={readOnly}
          vars={vars} multiline mono={false} spellCheck lang="es" explain={explain} ariaLabel="Instrucciones del agente"
          placeholder="Eres el asistente de… Tu trabajo es… Habla de forma… Nunca…  (escribe {{ para poner un dato)" minHeight={420} maxHeight="70vh" />
        {split && <div className="min-h-[240px] max-h-[50vh] xl:min-h-[420px] xl:max-h-[70vh]"><LivePreview text={text} scope={scope} hasPreTools={preTools.length > 0} ran={Boolean(run)} /></div>}
      </div>
      {error && <p role="alert" className="mt-2 text-[12.5px] text-signal">{error}</p>}
      <p className="mt-2 text-[12.5px] text-fg-faint">Se guarda solo mientras escribes (o con Ctrl+S). Escribe <code className="font-mono text-fg-dim">{'{{'}</code> para ver los datos que puedes poner, <code className="font-mono text-fg-dim">|</code> para los métodos y <code className="font-mono text-fg-dim">{'{{='}</code> para escribir código (Ctrl+Espacio abre las sugerencias). Los canales siguen con la versión publicada hasta que publiques.</p>
      <PromptTestData agent={agent} draft={draft} text={text} sample={sample} onSample={setSample} run={run} onRun={setRun} disabled={!engineReady} />
      <div className="mt-8">
        <PromptAssistant explain={explain} agent={agent} text={text} readOnly={readOnly} engineReady={engineReady} hasCredential={hasCredential} onApply={applyText} onGoCredential={onGoCredential} />
      </div>
    </div>
  );
}
